import React, { useState } from 'react';
import { styled } from 'styled-components';
import { ClusteringLabGroup, ClusteringLabMembership, ClusteringLabPhoto } from '../types';

const GROUP_COLORS = ['#4299e1', '#48bb78', '#ed8936', '#9f7aea', '#f56565', '#38b2ac', '#ed64a6', '#ecc94b'];

export function getGroupColor(groupId: string): string {
  let hash = 0;
  for (let i = 0; i < groupId.length; i++) {
    hash = (hash * 31 + groupId.charCodeAt(i)) | 0;
  }
  return GROUP_COLORS[Math.abs(hash) % GROUP_COLORS.length];
}

interface GroupPanelProps {
  groups: ClusteringLabGroup[];
  memberships: ClusteringLabMembership[];
  photos: ClusteringLabPhoto[];
  currentGroupId: string | null;
  onDrillDown: (groupId: string) => void;
  onRename: (groupId: string, label: string) => void;
  onDelete: (groupId: string) => void;
}

const Panel = styled.div`
  width: 240px;
  min-width: 240px;
  border-left: 1px solid #e2e8f0;
  padding: 12px;
  overflow-y: auto;
  background: #f7fafc;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #4a5568;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const GroupCard = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  border-left: 4px solid ${(p) => p.$color};
  background: white;
  margin-bottom: 6px;

  &:hover {
    background: #edf2f7;
  }
`;

const GroupThumb = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const GroupInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const GroupLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #2d3748;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const GroupCount = styled.div`
  font-size: 11px;
  color: #a0aec0;
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  color: #cbd5e0;
  cursor: pointer;
  font-size: 14px;
  padding: 2px 4px;
  flex-shrink: 0;

  &:hover {
    color: #e53e3e;
  }
`;

const LabelInput = styled.input`
  font-size: 13px;
  border: 1px solid #3182ce;
  border-radius: 3px;
  padding: 1px 4px;
  width: 100%;
  outline: none;
`;

const GroupPanel: React.FC<GroupPanelProps> = ({
  groups,
  memberships,
  photos,
  currentGroupId,
  onDrillDown,
  onRename,
  onDelete,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const childGroups = groups.filter((g) => g.parent_group_id === currentGroupId);
  const memberCounts = new Map<string, number>();
  memberships.forEach((m) => {
    memberCounts.set(m.group_id, (memberCounts.get(m.group_id) || 0) + 1);
  });

  const photoUrlMap = new Map(photos.map((p) => [p.photo_id, p.photo_url]));

  const handleDoubleClick = (groupId: string, currentLabel: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(groupId);
    setEditValue(currentLabel);
  };

  const handleRenameSubmit = (groupId: string) => {
    if (editValue.trim()) {
      onRename(groupId, editValue.trim());
    }
    setEditingId(null);
  };

  return (
    <Panel>
      <Title>Groups ({childGroups.length})</Title>
      {childGroups.map((group) => {
        const color = getGroupColor(group.group_id);
        const count = memberCounts.get(group.group_id) || 0;
        const repUrl = group.representative_photo_id ? photoUrlMap.get(group.representative_photo_id) : undefined;

        return (
          <GroupCard key={group.group_id} $color={color} onClick={() => onDrillDown(group.group_id)}>
            {repUrl && <GroupThumb src={repUrl} />}
            <GroupInfo>
              {editingId === group.group_id ? (
                <LabelInput
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={() => handleRenameSubmit(group.group_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRenameSubmit(group.group_id);
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
              ) : (
                <GroupLabel onDoubleClick={(e) => handleDoubleClick(group.group_id, group.label, e)}>
                  {group.label}
                </GroupLabel>
              )}
              <GroupCount>{count} photos</GroupCount>
            </GroupInfo>
            <DeleteBtn
              onClick={(e) => {
                e.stopPropagation();
                onDelete(group.group_id);
              }}
              title="Delete group"
            >
              ✕
            </DeleteBtn>
          </GroupCard>
        );
      })}
    </Panel>
  );
};

export default GroupPanel;
